import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import bwipjs from 'bwip-js/browser';
import ReportShell, { reportStyle, formatReportTimestamp } from '../../shared/ReportShell';
import { useCompanyName } from '../../shared/useCompanyName';
import { formatDate } from '../../shared/tenantClock';
import { formatNumber } from '../../shared/numberFormat';
import LineOrderSwitch, { inLineOrder } from '../../shared/LineOrderSwitch';


// Printed so the Pick page (PickOrderPage.js) can scan a physical order
// straight to its record instead of typing the number by hand — see
// vis_order_fulfilment.md. Data Matrix (see std-technology-stack.md's
// Barcode Standard) rather than Code128 — a 2D symbology packs
// display_number's letters/digits/dashes into a much smaller square
// footprint than an equivalent-length linear barcode needs, which is what
// Code128 was running out of quiet-zone room for on tight labels.
function OrderBarcode({ value }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && value) {
      // toSVG (not toCanvas) keeps this infinitely crisp at print
      // resolution, same as the SVG JsBarcode used to render straight into
      // — a raster canvas would need its own DPI/scale tuned to the actual
      // printer, reintroducing a version of the reliability problem Data
      // Matrix was picked to solve. The literal value is printed as plain
      // text underneath (not bwip-js's own includetext) so the fallback
      // wording/sizing matches what this page already used for Code128.
      ref.current.innerHTML = bwipjs.toSVG({
        bcid: 'datamatrix', text: value, scale: 3, includetext: false,
      });
      // bwip-js's SVG carries a viewBox but no width/height attribute —
      // left alone, a block-level <svg> with no intrinsic size stretches
      // to fill the flex row it sits in (rendering the matrix far larger
      // than intended) instead of the small square the scale above
      // actually produces. Pin both explicitly from the viewBox itself so
      // the symbol renders at its real size regardless of container width.
      const svgEl = ref.current.querySelector('svg');
      const viewBox = svgEl?.getAttribute('viewBox');
      if (svgEl && viewBox) {
        // Scaled down to 81% of the viewBox's native size (two successive
        // 10% trims) — shrinking a vector SVG this way stays crisp at any
        // print resolution, unlike downscaling a raster image.
        const [, , w, h] = viewBox.split(' ').map(Number);
        svgEl.setAttribute('width', w * 0.81);
        svgEl.setAttribute('height', h * 0.81);
      }
    }
  }, [value]);
  return (
    <div style={{ maxWidth: '100%', textAlign: 'right' }}>
      <div ref={ref} style={{ display: 'inline-block' }} />
      <div style={{ fontSize: 12, marginTop: 2 }}>{value}</div>
    </div>
  );
}

// Two per printed row (see SkuCardRow below), one per distinct SKU on
// the order — a laser-printed stand-in for a real product label while
// Matrix barcodes are bedded down alongside legacy Code128 stock (see
// platform_settings.print_sku_cards_on_so). Smaller than OrderBarcode's own
// Data Matrix sizing (std-technology-stack.md's Barcode Standard, scale: 3 +
// 0.81 trim) on purpose — that size was tuned for a single isolated symbol,
// but a page of these sits several per row (see SkuCardRow below), and a
// picker's camera scan was catching the neighbouring card's code as well as
// the intended one. Shrinking the symbol itself (not just spacing the cards
// further apart) narrows how much of the camera's frame any one code can
// fill, on top of the row/column spacing bump below.
function SkuCard({ sku, name, barcodeValue }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && barcodeValue) {
      ref.current.innerHTML = bwipjs.toSVG({
        bcid: 'datamatrix', text: barcodeValue, scale: 2, includetext: false,
      });
      const svgEl = ref.current.querySelector('svg');
      const viewBox = svgEl?.getAttribute('viewBox');
      if (svgEl && viewBox) {
        const [, , w, h] = viewBox.split(' ').map(Number);
        svgEl.setAttribute('width', w * 0.7);
        svgEl.setAttribute('height', h * 0.7);
      }
    }
  }, [barcodeValue]);
  // Landscape, barcode-beside-text — approximates a typical small
  // Zebra-class product label's proportions (roughly 2:1) rather than the
  // portrait, barcode-over-text stack this started as, since the point is
  // reusing this same layout once Optim prints real labels.
  return (
    <div style={{ border: '1px solid #999', borderRadius: 8, padding: 16, width: 300, boxSizing: 'border-box', flexGrow: 0, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 16 }}>
      {barcodeValue ? <div ref={ref} style={{ flexShrink: 0 }} /> : (
        <div style={{ color: '#888', fontSize: 11, flexShrink: 0, textAlign: 'center', lineHeight: 1.4 }}>
          <div>No barcode</div>
          <div>on file</div>
        </div>
      )}
      <div style={{ textAlign: 'left', minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{sku}</div>
        <div style={{ fontSize: 12, marginTop: 2 }}>{name}</div>
      </div>
    </div>
  );
}

// Groups skuCards two-to-a-row. Only the first row forces a page break
// (.page-break-before, App.css) — the point is starting a fresh page after
// the line-items table, not one page per row. Later rows just flow down
// that page and overflow onto further pages on their own, the same natural
// pagination the line-items table above already relies on, ending up as a
// multi-row, multi-column grid of cards rather than one row per page.
// .page-break-avoid keeps a row's own two cards from being split across a
// page boundary; the odd final row is left as one card rather than
// stretched to fill the second slot.
function SkuCardRow({ cards, isFirst }) {
  return (
    <div
      className={isFirst ? 'page-break-before page-break-avoid' : 'page-break-avoid'}
      // space-between (not center) pushes a two-card row apart instead of
      // clustering both in the middle, so the gap between them doesn't
      // steal from the barcodes' own quiet zones — but flush against the
      // true page edges (60/64px in from before) was too far apart. The
      // horizontal inset below pulls both columns in from the edge while
      // keeping that same space-between separation. A lone odd-final card
      // still lands at the left inset, which is fine — nothing to space it
      // against. gap and paddingTop both widened alongside the smaller
      // matrix above (see SkuCard) — a scan aimed at one card was landing on
      // its neighbour's code, horizontally within a row and vertically
      // between rows once a page carries more than one.
      style={{ display: 'flex', justifyContent: 'space-between', gap: 80, padding: '0 90px', paddingTop: isFirst ? 60 : 64 }}
    >
      {cards.map((card) => <SkuCard key={card.sku} sku={card.sku} name={card.name} barcodeValue={card.barcodeValue} />)}
    </div>
  );
}

// Shared row height for a line's stacked UoM/Qty/Picked/Packed
// entries — one per breakdown row (see pickingBreakdown below) — so row N
// lines up across every column regardless of how many rows a line's
// breakdown produces.
const ROW_H = 22;

// Same multi-line letterhead address helpers as QuoteReportView.js (shared
// with the Packing List, PackingListReportView.js), kept as
// local copies here for the same reason that file gives (a print-report
// concern reading company_settings/address shapes, not a settings-form or
// shared-lookup concern).
export function companyAddressLines(data) {
  if (!data) return [];
  const unit = data.address_unit ? `${data.address_unit}, ` : '';
  const line1 = `${unit}${data.address_street || ''}`.trim();
  const line2 = [data.address_district, data.address_city, data.address_state, data.address_postcode].filter(Boolean).join(' ');
  return [line1, line2, data.country].filter(Boolean);
}

export function deliveryAddressLines(a) {
  if (!a) return [];
  const line1 = [a.line1, a.line2].filter(Boolean).join(', ');
  const line2 = [a.suburb, a.state, a.postcode].filter(Boolean).join(' ');
  return [line1, line2, a.country].filter(Boolean);
}

export function MetaRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '2px 0' }}>
      <span style={{ color: '#888', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}

// The Pick Slip (issues #295, #445) — the warehouse's working copy of an
// order, printed once before picking and ticked off by hand line by line.
// It stays in the warehouse and is never enclosed with the goods (that's
// the Packing List, #296, #445), so it can carry the order's internal
// comments and the Hold reason. Only what staff picking and packing act on:
// the order number and its barcode, whose goods they are (the owner's logo
// or name, so a 3PL client's order stands out), the Customer, date and
// reference on one line, the carrier, Service and Options on another, the
// lines, and the comments. No addresses, prices, availability or weights.
// Printed from the order's own screen (e.g. the Sales Order screen) and from
// the Pick Order screen (PickOrderPage.js). Any order type (issue #298): its
// title, number, owner, Customer and comments come from the order type
// through /api/fulfilment; the lines (what is still to pick) and barcode
// are the same for all.
function PickSlipReportView({ orderType, orderId, onClose }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [companySettings, setCompanySettings] = useState(null);
  const [platformSettings, setPlatformSettings] = useState(null);
  const { logoUrl } = useCompanyName();
  // SKU order on every open; Line sequence for this print only.
  const [lineOrder, setLineOrder] = useState('sku');

  useEffect(() => {
    setLoading(true);
    axios.get(`/api/fulfilment/${orderType}/${orderId}`)
      .then((res) => setOrder(res.data.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load the order'))
      .finally(() => setLoading(false));
    axios.get('/api/company-settings')
      .then((res) => setCompanySettings(res.data.data || null))
      .catch(() => setCompanySettings(null));
    axios.get('/api/platform-settings')
      .then((res) => setPlatformSettings(res.data.data || null))
      .catch(() => setPlatformSettings(null));
  }, [orderType, orderId]);

  // The slip is for picking only (issue #445): packing is worked on the
  // Pack screen, and the Packing List is the packing document. To Pick is
  // ordered, less cancelled, less already Picked. A slip printed before
  // picking lists the order; a reprint mid-way lists only what's left, so
  // nobody picks twice, and a later shipment's slip lists only the
  // shortfall (what shipped was picked). Lines with nothing left to pick
  // are left off.
  const orderedQty = (line) => Math.max(0, Number(line.qty || 0) - Number(line.qty_cancelled || 0));
  const leftToPick = (line) => Math.max(0, orderedQty(line) - Number(line.qty_picked || 0));
  const allLines = inLineOrder(order?.lines || [], lineOrder);
  const lines = allLines.filter((line) => leftToPick(line) > 0);

  const formatQty = (n) => formatNumber(Number(n || 0), { maxDecimals: 2 });

  // Peels the largest whole units first off a base-unit quantity (e.g. 25
  // Packs' worth of candidates BX(rate 100)/PK(rate 10) → 0 BX + 2 PK + 5
  // PK... i.e. 2 BX + 5 PK) — never a fractional unit, since there's no such
  // thing as half a Box.
  const peelWholeUnits = (baseQty, candidates) => {
    const rows = [];
    let remaining = baseQty;
    candidates.forEach((pu) => {
      const rate = Number(pu.conversion_rate);
      const whole = Math.floor(remaining / rate);
      if (whole > 0) {
        rows.push({ uomName: pu.uom?.code || pu.uom?.name, qty: whole, rate });
        remaining -= whole * rate;
      }
    });
    return rows;
  };

  // A line's rows to pick, one per unit, broken into the largest whole
  // sellable units first, never below the UoM the line was sold in — the
  // order was placed in that unit, so "0.9 BX" of it means nothing a picker
  // could act on differently from "1 BX". Each unit is a row to tick;
  // there's no base-unit total, since nobody picks to it. Returns
  // { rows: [{ uomName, qty }], done }:
  //   - nothing picked yet: the ordered rows;
  //   - part picked, and what's left still fits the ordered units: what's
  //     left of each unit, units with nothing left dropped (so nobody
  //     reads "0 BOX" and picks one);
  //   - part picked, and what's left no longer fits (a box was broken): one
  //     row in the smallest unit — staff chose to work in that unit for
  //     this order.
  // `done` is how much is picked "of" what was ordered, in the unit the line
  // was ordered in ("6 PCK of 8"). A pick is saved as a quantity,
  // not the unit scanned, so this never claims a box was picked.
  const pickingBreakdown = (line) => {
    const allUoms = line.product?.uoms || [];
    const soldUom = allUoms.find((pu) => String(pu.uom_id) === String(line.uom_id));
    const soldRate = soldUom ? Number(soldUom.conversion_rate) : 1;
    let candidates = allUoms
      .filter((pu) => pu.can_sell && Number(pu.conversion_rate) >= soldRate)
      .sort((a, b) => Number(b.conversion_rate) - Number(a.conversion_rate));
    if (!candidates.length) candidates = [{ uom: line.uom, conversion_rate: soldRate }];

    const orderedBase = Math.max(0, Number(line.qty || 0) - Number(line.qty_cancelled || 0)) * soldRate;
    const doneBase = Number(line.qty_picked || 0) * soldRate;
    const leftBase = Math.max(0, orderedBase - doneBase);
    const ordered = peelWholeUnits(orderedBase, candidates);
    if (doneBase <= 0) return { rows: ordered, done: null };

    const soldName = soldUom?.uom?.code || soldUom?.uom?.name || line.uom?.code || line.uom?.name || '';
    const doneText = `${formatQty(doneBase / soldRate)} ${soldName} of ${formatQty(orderedBase / soldRate)}`;
    const left = peelWholeUnits(leftBase, candidates);
    const orderedOf = new Map(ordered.map((r) => [r.uomName, r.qty]));
    const accounted = left.reduce((sum, r) => sum + r.qty * r.rate, 0);
    const fits = Math.abs(accounted - leftBase) < 1e-9 && left.every((r) => orderedOf.has(r.uomName) && r.qty <= orderedOf.get(r.uomName));
    if (fits) return { rows: left, done: doneText };

    const smallest = candidates[candidates.length - 1];
    const rate = Number(smallest.conversion_rate);
    return {
      rows: [{ uomName: smallest.uom?.code || smallest.uom?.name, qty: leftBase / rate, rate }],
      done: doneText,
    };
  };

  // One card per distinct SKU (not per line) — a SKU split across multiple
  // UoM lines only needs proving out once. Barcode prefers the line's own
  // sold UoM (what a picker would actually be scanning off the shelf), then
  // the base UoM, then any other UoM on the product that has one at all —
  // a bare base unit (e.g. a loose Piece) often carries no printed barcode
  // of its own, only its packaging (Pack/Box) does, so locking to base UoM
  // alone left this blank even when the product had a usable barcode.
  const skuCards = [];
  const seenSkus = new Set();
  lines.forEach((line) => {
    const sku = line.product?.sku;
    if (!sku || seenSkus.has(sku)) return;
    seenSkus.add(sku);
    const uoms = line.product?.uoms || [];
    const soldProductUom = uoms.find((pu) => String(pu.uom_id) === String(line.uom_id));
    const baseProductUom = uoms.find((pu) => String(pu.uom_id) === String(line.product?.base_uom_id));
    const anyBarcodedUom = uoms.find((pu) => pu.barcode);
    const barcodeValue = soldProductUom?.barcode || baseProductUom?.barcode || anyBarcodedUom?.barcode || '';
    skuCards.push({ sku, name: line.product?.name, barcodeValue });
  });
  // Whose goods these are: the business itself unless the order type names
  // someone else (a 3PL order's client), shown as its logo or its name.
  const ownerName = order?.sender?.name || companySettings?.company_name || '';
  const showLogo = !order?.sender && logoUrl;
  const muted = { color: '#555', fontSize: 13 };
  const orderLine = order ? [
    order.deliver_to?.name || order.party?.name,
    formatDate(order.order_date),
    order.reference ? `${order.reference.label} ${order.reference.value}` : null,
  ].filter(Boolean).join(' · ') : '';
  const shippingLine = order ? [
    order.carrier?.name || 'No carrier',
    order.shipping_service?.description || 'No Service',
    ...(order.selected_options || []).map((o) => o.shipping_option.description),
  ].join(' · ') : '';

  const skuCardRows = [];
  for (let i = 0; i < skuCards.length; i += 2) {
    skuCardRows.push(skuCards.slice(i, i + 2));
  }

  return (
    <ReportShell
      onClose={onClose}
      printDisabled={loading || !order}
      actions={lines.length > 1 && <LineOrderSwitch value={lineOrder} onChange={setLineOrder} />}
      hideRunningHeader
      hideConfidentialityNote
      hideLogo
      hideCompanyNameHeader
      beforeTitle={order && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            {/* Left block matches the barcode slot's width so the logo in
                the middle stays visually centered either side. Barcode slot
                width kept at its old CODE128 sizing — a square Data Matrix
                at this scale renders well inside it, so there's room to
                spare rather than a tight fit. */}
            <div style={{ width: 180 }}>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Pick Slip</div>
              <div style={{ fontSize: 11, color: '#888', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>{order.type_label} No.</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{order.display_number}</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              {showLogo
                ? <img src={logoUrl} alt={ownerName} style={{ maxHeight: 56, maxWidth: 260, objectFit: 'contain' }} />
                : <div style={{ fontSize: 16, fontWeight: 700 }}>{ownerName}</div>}
            </div>
            <div style={{ width: 180 }}>
              <OrderBarcode value={order.display_number} />
            </div>
          </div>
          {/* Heavy black border so it still stands out on a mono laser
              printer. Hold is order-level only for now (see #295). */}
          {order.on_hold && (
            <div style={{ border: '2px solid #000', borderRadius: 6, padding: '8px 12px', marginBottom: 16, fontSize: 14 }}>
              <strong style={{ letterSpacing: 0.6 }}>ON HOLD</strong>
              {order.on_hold_reason ? ` — ${order.on_hold_reason}` : ''}
            </div>
          )}
        </>
      )}
      footerNote={order ? formatReportTimestamp() : undefined}
    >
      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}

      {order && (
        <>
          <div style={{ ...muted, fontWeight: 600, color: '#222' }}>{orderLine}</div>
          {/* Options are extras the packer acts on, so they're named here. */}
          <div style={{ ...muted, marginBottom: 12 }}>{shippingLine}</div>

          {lines.length === 0 ? (
            <div style={{ marginTop: 20, fontSize: 14, fontWeight: 600 }}>
              {allLines.length ? 'Everything on this order has been picked.' : 'No lines.'}
            </div>
          ) : (
            <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: reportStyle.tableFontSize, marginTop: 8 }}>
              {/* Number columns sized to their figures and right-aligned
                  as one block; the spare width goes to Product. */}
              <colgroup>
                <col style={{ width: '4%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '55%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '11%' }} />
              </colgroup>
              <thead style={{ fontSize: reportStyle.tableFontSize }}>
                <tr style={{ textAlign: 'left', borderBottom: reportStyle.tableHeaderBorder }}>
                  <th style={{ padding: '6px 8px 6px 0' }} aria-label="Tick" />
                  <th style={{ padding: '6px 8px 6px 0' }}>SKU</th>
                  <th style={{ padding: '6px 8px 6px 0' }}>Product</th>
                  <th style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>UoM</th>
                  <th style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>To Pick</th>
                  <th style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>Picked</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => {
                  const { rows, done } = pickingBreakdown(line);
                  return (
                    <React.Fragment key={line.id}>
                      <tr style={{ borderBottom: '1px solid #ddd' }}>
                        {/* Box for ticking the line off by hand. */}
                        <td style={{ padding: '6px 8px 6px 0', verticalAlign: 'top' }}>
                          <div style={{ width: 14, height: 14, border: '1.5px solid #333', borderRadius: 2, marginTop: 3 }} />
                        </td>
                        <td style={{ padding: '6px 8px 6px 0', overflowWrap: 'break-word', verticalAlign: 'top' }}>
                          {line.product?.sku}
                        </td>
                        <td style={{ padding: '6px 8px 6px 0', verticalAlign: 'top' }}>
                          {line.product?.name}
                          {/* Black, not grey: grey prints badly on a mono laser. */}
                          {done && <div style={{ fontSize: 11, fontStyle: 'italic', marginTop: 2 }}>Already picked: {done}</div>}
                        </td>
                        <td style={{ padding: '6px 8px 6px 0', textAlign: 'right', verticalAlign: 'top' }}>
                          {rows.map((r, i) => <div key={i} style={{ height: ROW_H, lineHeight: `${ROW_H}px` }}>{r.uomName}</div>)}
                        </td>
                        <td style={{ padding: '6px 8px 6px 0', textAlign: 'right', verticalAlign: 'top' }}>
                          {rows.map((r, i) => <div key={i} style={{ height: ROW_H, lineHeight: `${ROW_H}px`, fontWeight: 600 }}>{formatQty(r.qty)}</div>)}
                        </td>
                        {/* Picked prints blank, a line per unit, for a tick
                            or a hand-written quantity, so nothing on the
                            slip changes as the work goes on. Progress shows
                            live on screen. */}
                        <td style={{ padding: '6px 8px 6px 0', verticalAlign: 'top' }}>
                          {rows.map((r, i) => <div key={i} style={{ height: ROW_H, borderBottom: '1px dotted #999', marginLeft: 8 }} />)}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Every comment the order type gives prints — the Pick Slip never
              leaves the warehouse, so a Sales Order's Staff Comment is safe
              here. Headings match the order screen's own labels
              (DocumentComments.js). */}
          {(order.comments || []).length > 0 && (
            <div style={{ fontSize: 13, marginTop: 6 }}>
              {order.comments.map((c) => (
                <React.Fragment key={c.label}>
                  <div style={{ color: '#888', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, marginBottom: 4, marginTop: 10, textTransform: 'uppercase' }}>{c.label}</div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{c.text}</div>
                </React.Fragment>
              ))}
            </div>
          )}

          {platformSettings?.print_sku_cards_on_so && skuCardRows.map((cards, i) => (
            <SkuCardRow key={i} cards={cards} isFirst={i === 0} />
          ))}
        </>
      )}
    </ReportShell>
  );
}

export default PickSlipReportView;
